import { calculateBestQuotes, getMarketQty, type QuoteMarketWithBestBidOffer } from '@src/utils/rfq';
import {
  BlotterTable,
  Button,
  ButtonVariants,
  FormControlSizes,
  IconName,
  isPricingModeRelative,
  isTerminalQuoteStatus,
  isValidQuotePrice,
  LIGHTEN_CELL_CLASSNAME,
  NumberVariants,
  QuoteStatusEnum,
  SideEnum,
  toBig,
  Tooltip,
  useBlotterObservables,
  useBlotterTable,
  type ColumnDef,
  type ExpiryCountdownParams,
  type PriceParams,
  type PricingModeEnum,
  type Quote,
  type QuoteMarketColumnParams,
  type QuoteStatusColumnParams,
  type SizeColumnParams,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import styled from 'styled-components';

import type { QuoteSweepingQuoteMarkets } from './useQuoteSweepingQuoteMarkets';

interface SweeperMarketsBlotterProps {
  quoteSweepingData: QuoteSweepingQuoteMarkets;
  side: SideEnum;
  /** Markets currently present in the sweeping basket, whether staged, rejected, pending, or filled. */
  selectedMarketNames: Set<string>;
  /** Markets with executed fills. These remain selected but cannot be toggled out of the basket. */
  lockedMarketNames: Set<string>;
  showAllDecimals: boolean;
  onToggleMarket: (marketName: string) => void;
}

interface SweeperMarketRow {
  rowID: string;
  quote: Quote | undefined;
  quoteMarket: QuoteMarketWithBestBidOffer;
  side: SideEnum;
  Symbol: string | undefined;
  Currency: string | undefined;
  PricingMode: PricingModeEnum | undefined;
  QuoteStatus: QuoteStatusEnum | undefined;
  Text: string | undefined;
  expiryTime: string | undefined;
  bid: QuoteMarketWithBestBidOffer;
  offer: QuoteMarketWithBestBidOffer;
  bidQty: string | undefined;
  offerQty: string | undefined;
  bidDisplayPx: string | undefined;
  offerDisplayPx: string | undefined;
  /** True when the market is already selected in the basket. Controls the Sweep button variant. */
  isInSweeper: boolean;
  /** True when the selected market has executed and should no longer be removable. */
  isLockedInSweeper: boolean;
}

interface SweeperMarketActionColumnValue {
  marketName: string;
  isInSweeper: boolean;
  notTradableTooltip: string | undefined;
}

const LIGHTEN_CELL_CLASS_RULES = {
  [LIGHTEN_CELL_CLASSNAME]: () => true,
};

export function SweeperMarketsBlotter({
  quoteSweepingData,
  side,
  selectedMarketNames,
  lockedMarketNames,
  showAllDecimals,
  onToggleMarket,
}: SweeperMarketsBlotterProps) {
  const rows = useMemo(() => {
    const { representativeQuote, sweeperQuotes } = quoteSweepingData;
    const quoteByMarket = new Map(sweeperQuotes.map(({ quote, quoteMarket }) => [quoteMarket.Market, quote]));
    const quoteMarkets = sweeperQuotes.map(({ quoteMarket }) => quoteMarket);
    const quoteMarketsWithBestBidOffer = calculateBestQuotes({
      quotes: quoteMarkets,
      pricingMode: representativeQuote.PricingMode,
      // The point of calc best here is to just show the user which the best response was. The point is _not_ to elevate
      // the best quote into a "Trade Best" button or similar. So since this is read-only, we want to show what the best quote
      // was irrespectively of them being terminal (filled, or even canceled)
      onlyActive: false,
    });

    return quoteMarketsWithBestBidOffer.map(quoteMarket =>
      newSweeperMarketRow(
        quoteByMarket.get(quoteMarket.Market),
        quoteMarket,
        side,
        selectedMarketNames.has(quoteMarket.Market),
        lockedMarketNames.has(quoteMarket.Market)
      )
    );
  }, [lockedMarketNames, quoteSweepingData, selectedMarketNames, side]);
  const columns = useSweeperMarketsColumns(side, onToggleMarket);
  const { dataObservable, errorObservable } = useBlotterObservables({ data: rows });
  const blotterSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<SweeperMarketRow>({
    dataObservable,
    errorObservable,
    columns,
    rowID: 'rowID',
    settings: blotterSettings,
  });

  return (
    <SweeperMarketsBlotterWrapper>
      <BlotterTable {...blotterTable} />
    </SweeperMarketsBlotterWrapper>
  );
}

const SweeperMarketsBlotterWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

function useSweeperMarketsColumns(
  side: SideEnum,
  onToggleMarket: SweeperMarketsBlotterProps['onToggleMarket']
): ColumnDef<SweeperMarketRow>[] {
  return useMemo<ColumnDef<SweeperMarketRow>[]>(() => {
    const shouldShowBidColumns = side === SideEnum.Sell;

    return [
      getSweeperMarketActionColumn(onToggleMarket),
      {
        field: 'expiryTime',
        type: 'expiryCountdown',
        title: 'Expiry',
        initialWidth: 130,
        params: {
          isExpired: (data: unknown) =>
            isSweeperMarketRow(data) && !!data.QuoteStatus && isTerminalQuoteStatus(data.QuoteStatus),
        } satisfies ExpiryCountdownParams<SweeperMarketRow>,
      },
      {
        type: 'quoteMarket',
        field: 'bid.Market',
        title: 'Bid Maker',
        initialWidth: 180,
        hide: !shouldShowBidColumns,
        cellClassRules: LIGHTEN_CELL_CLASS_RULES,
        params: {
          isBest: data => data?.bid.isBestBid ?? false,
        } satisfies QuoteMarketColumnParams<SweeperMarketRow>,
      },
      {
        type: 'size',
        field: 'bidQty',
        id: 'BidQty',
        title: 'Bid Qty',
        initialWidth: 170,
        hide: !shouldShowBidColumns,
        cellClassRules: LIGHTEN_CELL_CLASS_RULES,
        params: {
          currencyField: 'Currency',
          getSentiment: ({ data }) => getSizeCounteredSentiment(data?.quoteMarket.BidQty, data?.quote?.OrderQty),
        } satisfies SizeColumnParams<SweeperMarketRow>,
      },
      {
        type: 'price',
        field: 'bidDisplayPx',
        id: 'BidPx',
        title: 'Bid Px',
        initialWidth: 100,
        hide: !shouldShowBidColumns,
        cellClassRules: LIGHTEN_CELL_CLASS_RULES,
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
        } satisfies PriceParams<SweeperMarketRow>,
      },
      {
        type: 'quoteMarket',
        field: 'offer.Market',
        title: 'Offer Maker',
        initialWidth: 180,
        hide: shouldShowBidColumns,
        cellClassRules: LIGHTEN_CELL_CLASS_RULES,
        params: {
          isBest: data => data?.offer.isBestOffer ?? false,
        } satisfies QuoteMarketColumnParams<SweeperMarketRow>,
      },
      {
        type: 'size',
        field: 'offerQty',
        id: 'OfferQty',
        title: 'Offer Qty',
        initialWidth: 170,
        hide: shouldShowBidColumns,
        cellClassRules: LIGHTEN_CELL_CLASS_RULES,
        params: {
          currencyField: 'Currency',
          getSentiment: ({ data }) => getSizeCounteredSentiment(data?.quoteMarket.OfferQty, data?.quote?.OrderQty),
        } satisfies SizeColumnParams<SweeperMarketRow>,
      },
      {
        type: 'price',
        field: 'offerDisplayPx',
        title: 'Offer Px',
        id: 'OfferPx',
        initialWidth: 100,
        hide: shouldShowBidColumns,
        cellClassRules: LIGHTEN_CELL_CLASS_RULES,
        params: {
          assetField: 'Symbol',
          isReferencePrice: data => isPricingModeRelative(data?.PricingMode),
          increment: data => (isPricingModeRelative(data?.PricingMode) ? '0.00' : undefined),
        } satisfies PriceParams<SweeperMarketRow>,
      },
      {
        field: 'QuoteStatus',
        type: 'quoteStatus',
        title: 'Status',
        initialWidth: 150,
        params: {
          tooltipField: 'Text',
        } satisfies QuoteStatusColumnParams<SweeperMarketRow>,
      },
    ] satisfies ColumnDef<SweeperMarketRow>[];
  }, [onToggleMarket, side]);
}

function getSweeperMarketActionColumn(
  onToggleMarket: SweeperMarketsBlotterProps['onToggleMarket']
): ColumnDef<SweeperMarketRow> {
  return {
    type: 'custom',
    id: 'sweepAction',
    title: '',
    width: 75,
    pinned: 'right',
    sortable: false,
    params: {
      valueGetter: (params: ValueGetterParams<SweeperMarketRow>): SweeperMarketActionColumnValue | undefined => {
        if (!params.data) {
          return undefined;
        }

        return {
          marketName: params.data.quoteMarket.Market,
          isInSweeper: params.data.isInSweeper,
          notTradableTooltip: getNotTradableTooltip(params.data),
        };
      },
      cellRenderer: ({ value }: ICellRendererParams<SweeperMarketRow, SweeperMarketActionColumnValue>) => {
        if (!value) {
          return <></>;
        }

        const buttonDisabled = value.isInSweeper || value.notTradableTooltip != null;
        const button = (
          <Button
            size={FormControlSizes.Tiny}
            startIcon={IconName.Scale}
            disabled={buttonDisabled}
            onClick={() => onToggleMarket(value.marketName)}
            data-testid={`sweeper-market-${value.marketName}-toggle`}
            variant={buttonDisabled ? ButtonVariants.Default : ButtonVariants.Primary}
          >
            Sweep
          </Button>
        );

        return (
          <Tooltip tooltip={value.notTradableTooltip} usePortal={true}>
            {button}
          </Tooltip>
        );
      },
    } satisfies ColDef<SweeperMarketRow>,
  };
}

function getNotTradableTooltip(row: SweeperMarketRow): string | undefined {
  const price = row.side === SideEnum.Buy ? row.quoteMarket.OfferPx : row.quoteMarket.BidPx;

  if (row.isLockedInSweeper) {
    return 'This market has already been filled.';
  }

  if (row.quote?.isTerminal) {
    return 'This quote is terminal.';
  }

  if (row.QuoteStatus !== QuoteStatusEnum.Open) {
    return 'This quote is not tradable.';
  }

  if (!isValidQuotePrice(price, row.QuoteStatus, row.PricingMode)) {
    return 'This quote is not tradable.';
  }

  return undefined;
}

function isSweeperMarketRow(data: unknown): data is SweeperMarketRow {
  return typeof data === 'object' && data != null && 'QuoteStatus' in data;
}

function getSizeCounteredSentiment(
  marketQty: string | undefined,
  orderQty: string | undefined
): NumberVariants | undefined {
  const marketQtyBig = toBig(marketQty);
  const orderQtyBig = toBig(orderQty);

  return marketQtyBig && orderQtyBig && marketQtyBig.lt(orderQtyBig) ? NumberVariants.Warning : undefined;
}

function newSweeperMarketRow(
  quote: Quote | undefined,
  quoteMarket: QuoteMarketWithBestBidOffer,
  side: SideEnum,
  isInSweeper: boolean,
  isLockedInSweeper: boolean
): SweeperMarketRow {
  const bidDisplayPx = isValidQuotePrice(quoteMarket.BidPx, quoteMarket.QuoteStatus, quote?.PricingMode)
    ? quoteMarket.BidPx
    : undefined;
  const offerDisplayPx = isValidQuotePrice(quoteMarket.OfferPx, quoteMarket.QuoteStatus, quote?.PricingMode)
    ? quoteMarket.OfferPx
    : undefined;

  return {
    rowID: quoteMarket.Market,
    quote,
    quoteMarket,
    side,
    Symbol: quote?.Symbol,
    Currency: quote?.Currency,
    PricingMode: quote?.PricingMode,
    QuoteStatus: quoteMarket.QuoteStatus,
    Text: quoteMarket.Text,
    expiryTime: quoteMarket.ValidUntilTime,
    bid: quoteMarket,
    offer: quoteMarket,
    bidQty: getMarketQty(quoteMarket.BidQty, quote?.OrderQty, bidDisplayPx),
    offerQty: getMarketQty(quoteMarket.OfferQty, quote?.OrderQty, offerDisplayPx),
    bidDisplayPx,
    offerDisplayPx,
    isInSweeper,
    isLockedInSweeper,
  };
}
